import {
  readRichText,
  richTextPlainText,
  renderRichText,
  type RichTextNode,
} from "./rich-text-runtime";

// The runtime half of formatted section text (see page-formatted-text.ts), with no schema library,
// so a creator site's script stays small.

const blocks = new Set([
  "doc",
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "listItem",
  "text",
  "hardBreak",
]);
const marks = new Set(["bold", "italic", "underline", "link"]);

/** Section prose uses the story grammar without embeds, media, tables or authored markup. */
export function isPageText(root: RichTextNode) {
  const visit = (node: RichTextNode): boolean =>
    blocks.has(node.type) &&
    (node.marks ?? []).every((mark) => marks.has(mark.type)) &&
    (node.content ?? []).every(visit);
  return visit(root) && richTextPlainText(root).length <= 8000;
}

export function readTextFormat(value: string | undefined): RichTextNode | undefined {
  if (!value) return undefined;
  try {
    const document = readRichText(JSON.parse(value));
    return document && isPageText(document) ? document : undefined;
  } catch {
    return undefined;
  }
}

/** An edit to plain words elsewhere takes precedence over formatting of older words. */
export function matchingTextFormat(value: string | undefined, text: string) {
  const document = readTextFormat(value);
  return document && richTextPlainText(document).trim() === text.trim() ? document : undefined;
}

export function formattedTextHtml(
  value: string,
  text: string,
  inline = false,
  resolve: (text: string) => string = (text) => text,
): string | undefined {
  const document = matchingTextFormat(value, text);
  if (!document) return undefined;
  if (inline && (document.content?.length !== 1 || document.content[0]?.type !== "paragraph"))
    return undefined;
  const resolved = (node: RichTextNode): RichTextNode => ({
    ...node,
    ...(node.text ? { text: resolve(node.text) } : {}),
    ...(node.content ? { content: node.content.map(resolved) } : {}),
  });
  const html = renderRichText(resolved(document));
  return inline ? html.replace(/^<p(?: class="[^"]*")?>/, "").replace(/<\/p>$/, "") : html;
}
